import type { ReactNode } from 'react'
import React, { useContext as useContextCore, useMemo, useState } from 'react'
import { OrderCheckoutActivePanelEnum } from '@ztechapps/order.common'
import type { Components } from './components'
import { defaultComponents } from './components'
import type { CheckoutErrors, PaymentOptions, ShippingOptions } from './types'
import { roundNumber } from '@ztechapps/common.utils'
import isEmpty from 'lodash/isEmpty'
import type { Order } from '@ztechapps/common.contract'
import { PaymentMethodEnum } from '@ztechapps/common.contract'
import merge from 'lodash/merge'
import type { CheckoutStep } from './checkoutSteps'
import { getDefaultCheckoutSteps } from './checkoutSteps'
import type { State, UseDocumentResult } from '@ztechapps/common.hook'
import first from 'lodash/first'
import { useCheckoutErrors } from './hooks/useCheckoutErrors'
import { useShippingOptions } from './hooks/useShippingOptions'
import { usePaymentOptions } from './hooks/usePaymentOptions'
import isNil from 'lodash/isNil'
import { singleOrderQuery, useOrderDocument } from '@ztechapps/order.common'
import type { DocumentNode } from 'graphql/language'

export type ProviderProps = {
  children: ReactNode
  components?: Partial<Components>
  orderId: string
  //The order read; a storefront Customer passes `getMyOrderQuery`, staff keep the default `singleOrderQuery`
  orderQuery?: DocumentNode
  appSettings?: {
    RootUrl: string
    Stripe: {
      publishableKey: string
    }
  }
  success_url?: string | 'USE_CURRENT'
  cancel_url?: string | 'USE_CURRENT'
  createAchSourceUrl?: string
  paymentMethods?: string[]
  canOrder?: boolean
  isBelowMinimumOrderAmount?: boolean
  showProgress?: boolean
  fromCheckout?: boolean
  fromStore?: boolean
  checkoutStepsKeys?: OrderCheckoutActivePanelEnum[]
  defaultActivePanel?: OrderCheckoutActivePanelEnum
  onSuccess?: () => Promise<void>
}

export type ContextType = {
  activePanel: State<OrderCheckoutActivePanelEnum>
  showLoadingOverlay: State<boolean>
  shippingOptions: State<ShippingOptions>
  orderNetAmount: number
  hasAddress: boolean
  paymentMethods: State<PaymentMethodEnum[]>
  hasPartialPayments: boolean
  hasShipment: boolean
  checkoutSteps: CheckoutStep[]
  showBackButton: boolean
  orderDocumentResult: UseDocumentResult<Order>
  checkoutErrors: CheckoutErrors
  isCurrentCheckoutStepTheFirstStep: boolean
  message: State<string>
  paymentOptions: State<PaymentOptions>
  packagesReseedVersion: number
} & Omit<ProviderProps, 'children' | 'defaultActivePanel' | 'paymentMethods'>

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const CheckoutProvider = ({
  children,
  defaultActivePanel = OrderCheckoutActivePanelEnum.item,
  ...props
}: ProviderProps) => {
  //we need to initialize the states here coming from thr props
  const components = merge({}, defaultComponents, props.components)

  const orderDocument = useOrderDocument({ query: props.orderQuery ?? singleOrderQuery, _id: props.orderId })

  const activePanel = useState(defaultActivePanel)
  const showLoadingOverlay = useState(false)
  const checkoutErrors = useCheckoutErrors()
  const message = useState('')
  const [packagesReseedVersion, setPackagesReseedVersion] = useState(0)
  const shippingOptions = useShippingOptions({
    orderDocument,
    activePanel: activePanel[0],
    onPackagesReseed: () => setPackagesReseedVersion((current) => current + 1),
  })
  const paymentMethods = useState<PaymentMethodEnum[]>(
    (props.paymentMethods ?? [
      PaymentMethodEnum.card,
      PaymentMethodEnum.cash,
      PaymentMethodEnum.achDirectDebit,
    ]) as PaymentMethodEnum[]
  )
  const paymentOptions = usePaymentOptions(orderDocument)

  const orderNetAmount = useNetAmount(shippingOptions[0])
  const hasAddress = useHasAddress(orderDocument)
  const hasPartialPayments = useHasPartialPayments(orderDocument)
  const hasShipment = useHasShipment(orderDocument, shippingOptions[0])
  const _checkoutStepsKeys = useCheckoutStepsKeys(components)
  const checkoutSteps = useCheckoutSteps(_checkoutStepsKeys, components)
  const showBackButton = useShowBackButton(checkoutSteps)
  const isCurrentCheckoutStepTheFirstStep = useIsCurrentCheckoutStepTheFirstStep(checkoutSteps, activePanel[0])

  return (
    <CoreProvider
      value={{
        ...props,
        activePanel,
        showLoadingOverlay,
        shippingOptions,
        orderNetAmount,
        hasAddress,
        paymentMethods,
        components,
        hasPartialPayments,
        hasShipment,
        checkoutSteps,
        showBackButton,
        orderDocumentResult: orderDocument,
        checkoutErrors,
        isCurrentCheckoutStepTheFirstStep,
        message,
        paymentOptions,
        packagesReseedVersion,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useCheckoutContext = () => {
  if (!context) {
    throw new Error('CheckoutContext must be available inside CheckoutProvider')
  }
  return useContextCore(context)
}

export const useIsCurrentCheckoutStepTheFirstStep = (
  checkoutSteps: CheckoutStep[],
  activePanel: OrderCheckoutActivePanelEnum
) => {
  return useMemo<boolean>(() => {
    const items = checkoutSteps

    const firstStep = first(items)?.itemKey ?? 0
    return firstStep === activePanel
  }, [checkoutSteps, activePanel])
}

export const useCheckoutStepsKeys = (components: Components) => {
  return useMemo<OrderCheckoutActivePanelEnum[]>(() => {
    return getDefaultCheckoutSteps(components).map(({ itemKey }) => itemKey)
  }, [components])
}
export const useCheckoutSteps = (checkoutStepsKeys: OrderCheckoutActivePanelEnum[], components: Components) => {
  return useMemo(() => {
    const keys = checkoutStepsKeys
    const checkoutSteps = getDefaultCheckoutSteps(components)
    //we filter it base on keys
    return checkoutSteps.filter(({ itemKey }) => keys.includes(itemKey))
  }, [checkoutStepsKeys, components])
}

export const useHasShipment = (order: UseDocumentResult<Order>, shippingOptions: ShippingOptions) => {
  return useMemo(() => {
    const document = order?.document

    const isManualShippingRate = shippingOptions.isManualShippingRate
    const shipmentId = document?.shipping?.shipmentId

    return !isManualShippingRate && !!shipmentId
  }, [shippingOptions, order])
}

export const useHasPartialPayments = (order: UseDocumentResult<Order>) => {
  return useMemo(() => {
    const document = order?.document
    const balance = document?.paymentBalance?.balance
    const netAmount = document?.net
    //has partial payments when the balance is not zero and less than the net amount
    return !isNil(balance) && balance > 0 && balance < netAmount
  }, [order])
}
export const useHasAddress = (order: UseDocumentResult<Order>) => {
  return useMemo(() => {
    //check from the initial value, if it was initialized then used it, otherwise use the value from the document
    return !isEmpty(order?.document?.shippingAddress?.fullAddress)
  }, [order])
}
export const useNetAmount = (shippingOptions: ShippingOptions) => {
  return useMemo(() => {
    const gross = shippingOptions.gross
    const discount = shippingOptions.discount
    const taxAmount = shippingOptions.taxAmount
    const shippingFee = shippingOptions.shippingFee
    return roundNumber(gross + taxAmount - discount + shippingFee, 2)
  }, [shippingOptions])
}
export const useShowBackButton = (checkoutSteps: CheckoutStep[]) => {
  return useMemo(() => {
    return (checkoutSteps?.length ?? 0) > 1
  }, [checkoutSteps])
}
